type PassengerProps = {
  adults: number
  children: number
  onAdultsChange: (value: number) => void
  onChildrenChange: (value: number) => void
}

type CounterProps = {
  value: number
  minimum?: number
  onChange: (value: number) => void
}

function Counter({
  value,
  minimum = 0,
  onChange,
}: CounterProps) {
  const canDecrease = value > minimum

  return (
    <div className="flex items-center gap-[13px]">
      <button
        type="button"
        disabled={!canDecrease}
        onClick={() => {
          if (canDecrease) {
            onChange(value - 1)
          }
        }}
        aria-label="Decrease"
        className="
          flex
          h-[34px]
          w-[34px]
          shrink-0
          items-center
          justify-center
          rounded-full
          bg-[#dddddd]
          font-poppins
          text-[25px]
          font-light
          leading-none
          text-black
          disabled:cursor-not-allowed
          disabled:opacity-40
        "
      >
        <span className="-mt-[3px]">−</span>
      </button>

      <span className="min-w-[16px] text-center font-poppins text-[16px] font-medium text-black">
        {value}
      </span>

      <button
        type="button"
        onClick={() => onChange(value + 1)}
        aria-label="Increase"
        className="
          flex
          h-[34px]
          w-[34px]
          shrink-0
          items-center
          justify-center
          rounded-full
          bg-[#b8de79]
          font-poppins
          text-[25px]
          font-light
          leading-none
          text-black
          transition-transform
          hover:scale-105
        "
      >
        <span className="-mt-[2px]">+</span>
      </button>
    </div>
  )
}

function CheckedBagIcon() {
  return (
    <svg
      width="22"
      height="20"
      viewBox="0 0 26 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M9 5V3.8C9 2.8 9.8 2 10.8 2H15.2C16.2 2 17 2.8 17 3.8V5"
        stroke="#666666"
        strokeWidth="2"
      />

      <rect
        x="2"
        y="5"
        width="22"
        height="16"
        rx="2"
        fill="#666666"
      />

      <path
        d="M2 11H24"
        stroke="white"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function CarryBagIcon() {
  return (
    <svg
      width="23"
      height="25"
      viewBox="0 0 26 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <rect
        x="9"
        y="3"
        width="9"
        height="22"
        rx="2"
        stroke="#666666"
        strokeWidth="2"
      />

      <path
        d="M11 3V1.5M16 3V1.5"
        stroke="#666666"
        strokeWidth="2"
      />

      <rect
        x="2"
        y="9"
        width="6"
        height="15"
        rx="1.5"
        stroke="#666666"
        strokeWidth="2"
      />

      <rect
        x="19"
        y="7"
        width="5"
        height="17"
        rx="1.5"
        stroke="#666666"
        strokeWidth="2"
      />

      <circle
        cx="5"
        cy="26"
        r="1.5"
        fill="#666666"
      />

      <circle
        cx="13.5"
        cy="26"
        r="1.5"
        fill="#666666"
      />

      <circle
        cx="21.5"
        cy="26"
        r="1.5"
        fill="#666666"
      />
    </svg>
  )
}

export default function Passenger({
  adults,
  children,
  onAdultsChange,
  onChildrenChange,
}: PassengerProps) {
  return (
    <div
      role="dialog"
      aria-label="Select passengers"
      className="
        absolute
        right-0
        top-[calc(100%+16px)]
        z-[120]
        h-auto
        w-[740px]
        max-w-[calc(100vw-32px)]
        rounded-[16px]
        bg-white
        px-[20px]
        py-[25px]
        shadow-[0_10px_30px_rgba(0,0,0,0.28)]
      "
    >
      <div className="flex flex-col sm:flex-row gap-8 sm:gap-12">
        {/* Left Column: Counters */}
        <div className="flex-1 min-w-[280px]">
          <div className="space-y-[20px]">
            {/* Adults */}
            <div className="flex items-center justify-between gap-[16px]">
              <div>
                <p className="font-poppins text-[17px] font-semibold leading-tight text-black">
                  Adults
                </p>

                <p className="mt-[2px] font-poppins text-[14px] font-medium leading-tight text-[#999999]">
                  Age 12+
                </p>
              </div>

              <Counter
                value={adults}
                minimum={1}
                onChange={onAdultsChange}
              />
            </div>

            {/* Children */}
            <div className="flex items-center justify-between gap-[16px]">
              <div>
                <p className="font-poppins text-[17px] font-semibold leading-tight text-black">
                  Children
                </p>

                <p className="mt-[2px] font-poppins text-[14px] font-medium leading-tight text-[#999999]">
                  Age 0–12
                </p>
              </div>

              <Counter
                value={children}
                minimum={0}
                onChange={onChildrenChange}
              />
            </div>
          </div>
        </div>

        {/* Vertical Divider (hidden on mobile) */}
        <div className="hidden sm:block w-px bg-slate-200" />

        {/* Right Column: Luggage Info */}
        <div className="flex-1">
          <h3 className="font-poppins text-[16px] font-semibold text-black">
            Each Passenger is allowed
          </h3>

          <div className="mt-[18px] space-y-[16px]">
            <div className="flex items-center justify-between gap-[10px]">
              <div className="flex min-w-0 items-center gap-[10px]">
                <CheckedBagIcon />
                <span className="whitespace-nowrap font-poppins text-[15px] font-medium text-[#555555]">
                  One checked bag
                </span>
              </div>
              <span className="whitespace-nowrap rounded-full border border-[#3478ff] px-[10px] py-[4px] font-poppins text-[11px] font-medium text-black">
                29 × 21 × 11 inch
              </span>
            </div>

            <div className="flex items-center justify-between gap-[10px]">
              <div className="flex min-w-0 items-center gap-[10px]">
                <CarryBagIcon />
                <span className="whitespace-nowrap font-poppins text-[15px] font-medium text-[#555555]">
                  One carry-on bag
                </span>
              </div>
              <span className="whitespace-nowrap rounded-full border border-[#3478ff] px-[10px] py-[4px] font-poppins text-[11px] font-medium text-black">
                21 × 14 × 9 inch
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}